import React from 'react';
import {useMemoizedValue} from '@sail/react';
import {useController, createFormControl} from '../../lib/react-hook-form';
import type {View} from '../../view';
import {useFormContext} from './useFormContext';
import type {
  FieldValues,
  ControllerFieldState,
  UseFormStateReturn,
  UseControllerProps,
} from './types';

const DUMMY_CONTROL = createFormControl();

export type FormFieldValue =
  | string
  | number
  | boolean
  | string[]
  | readonly string[]
  | null
  | File
  | undefined;

export type FormAdapter<TValue = FormFieldValue, TValueOutput = TValue> = {
  /**
   * Optional adapter function to transform the field value before passing it to the component.
   * This enables custom value logic (e.g., checkbox groups where fieldValue is an array
   * but component needs a boolean).
   */
  valueAdapter: (fieldValue: TValue, controlValue?: TValue) => TValueOutput;
  /**
   * Optional adapter function to transform the change event before updating the field.
   * This enables custom onChange logic (e.g., checkbox groups where individual checkbox
   * changes need to update an array).
   */
  changeAdapter: (
    eventOrValue: React.ChangeEvent<HTMLElement> | TValueOutput,
    fieldValue: TValue,
    controlValue?: TValue,
  ) => TValue;
};

type BaseComponentProps = {
  disabled: boolean;
  error: string | undefined;
  onBlur?: () => void;
  name?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref?: any;
};

type ComponentProps<
  TProps extends FormBaseProps,
  TOmitValue extends boolean = false,
> = (TOmitValue extends true
  ? Omit<TProps, 'rules' | 'value' | 'onChange'>
  : Omit<TProps, 'rules' | 'onChange'>) &
  BaseComponentProps;

// TODO: Reassess this type defintion once we merge useFormRegister with useFormFieldProps.
// In any case, this type definition is only here to make useFormRegister happy internally.
type FormBaseProps = UseControllerProps<FieldValues> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inherits?: View.Inherits<any>;
};

type UseFormRegisterProps<
  TProps extends FormBaseProps,
  TValueProp extends string = 'value',
  TChangeProp extends string = 'onChange',
  TComponentValue = FormFieldValue,
> = {
  props: TProps;
  valueProp?: TValueProp;
  changeProp?: TChangeProp;
  valueAdapter?: FormAdapter<FormFieldValue, TComponentValue>['valueAdapter'];
  changeAdapter?: FormAdapter<FormFieldValue, TComponentValue>['changeAdapter'];
};

type UseFormRegisterReturn<TProps extends FormBaseProps> =
  | {
      isInSailForm: true;
      componentProps:
        | ComponentProps<TProps, false>
        | ComponentProps<TProps, true>;
      fieldState: ControllerFieldState;
      formState: UseFormStateReturn<FieldValues>;
    }
  | {
      isInSailForm: false;
      componentProps: TProps;
      fieldState: object;
      formState: object;
    };

export const useFormRegister = <
  TProps extends FormBaseProps,
  TValueProp extends string = 'value',
  TChangeProp extends string = 'onChange',
  TComponentValue = FormFieldValue,
>({
  props: originalProps,
  valueProp = 'value' as TValueProp,
  changeProp = 'onChange' as TChangeProp,
  valueAdapter,
  changeAdapter,
}: UseFormRegisterProps<
  TProps,
  TValueProp,
  TChangeProp,
  TComponentValue
>): UseFormRegisterReturn<TProps> => {
  const form = useFormContext();
  const props = useMemoizedValue(originalProps);

  const {field, fieldState, formState} = useController({
    control: form?.control || DUMMY_CONTROL.control,
    rules: props.rules,
    name: props.name || '__dummy__',
    defaultValue: props.defaultValue,
  });

  const adaptedValue = React.useMemo(() => {
    return valueAdapter
      ? valueAdapter(field.value, props.value)
      : (field.value as TComponentValue);
  }, [valueAdapter, field.value, props.value]);

  const adaptedOnChange = React.useCallback(
    (event: React.ChangeEvent<HTMLElement>) => {
      const transformedValue = changeAdapter
        ? changeAdapter(event, field.value, props.value)
        : event;

      field.onChange(transformedValue);

      const propsOnChangeCallback = props[changeProp as keyof TProps];
      if (typeof propsOnChangeCallback === 'function') {
        propsOnChangeCallback(event);
      }
    },
    [changeAdapter, changeProp, field, props],
  );

  const componentProps = React.useMemo(() => {
    // 1. Remove rules object, we don't want it in the component.
    // 2. Remove onChange, we're handling it in [changeProp].
    // 3. Remove value, but only if valueProp is 'value'. Otherwise, we want
    //    both props (value and [valueProp]) to be available to the component.
    //    Certain components (e.g., Radio) need both props to be available to
    //    the component.
    const restProps =
      valueProp === 'value'
        ? (({value, onChange, rules, ...rest}) => rest)(props)
        : (({onChange, rules, ...rest}) => rest)(props);

    // Also remove value and onChange from field, as we're handling them in
    // [valueProp] and [changeProp].
    const restField = (({onChange, value, ...rest}) => rest)(field);

    return {
      ...restProps,
      [valueProp]: adaptedValue,
      [changeProp]: adaptedOnChange,
      disabled: props.disabled || formState.disabled,
      error: fieldState.error?.message,
      ...restField,
      // We pass original inherits prop because we must maintain exact object
      // identity.
      inherits: originalProps.inherits,
    };
  }, [
    props,
    field,
    formState.disabled,
    fieldState.error?.message,
    valueProp,
    changeProp,
    adaptedValue,
    adaptedOnChange,
    originalProps.inherits,
  ]);

  // When not in a Sail Form context or name not provided, act as noop.
  // Return props as-is.
  if (!form?.control || !props.name) {
    return {
      isInSailForm: false,
      componentProps: {
        ...props,
        inherits: originalProps.inherits,
      },
      fieldState: {},
      formState: {},
    };
  }

  return {
    isInSailForm: true,
    componentProps,
    fieldState,
    formState,
  };
};
